import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";
import { Icon, type IconName } from "../icon/Icon";

type Variant = "solid" | "outlined" | "text";
type Color = "primary" | "assistive";
type Size = "large" | "medium" | "small" | "tiny";

const SIZES: Record<
  Size,
  { height: number; padX: number; font: string; lh: string; radius: string; icon: number; gap: number }
> = {
  large: {
    height: 48,
    padX: 20,
    font: "var(--font-size-body-1)",
    lh: "var(--line-height-body-1)",
    radius: "var(--radius-md)",
    icon: 20,
    gap: 4,
  },
  medium: {
    height: 40,
    padX: 16,
    font: "var(--font-size-body-2)",
    lh: "var(--line-height-body-2)",
    radius: "var(--radius-md)",
    icon: 18,
    gap: 4,
  },
  small: {
    height: 32,
    padX: 14,
    font: "var(--font-size-label-1)",
    lh: "var(--line-height-label-1)",
    radius: "var(--radius-sm)",
    icon: 16,
    gap: 2,
  },
  tiny: {
    height: 24,
    padX: 10,
    font: "var(--font-size-caption-1)",
    lh: "var(--line-height-caption-1)",
    radius: "var(--radius-xs)",
    icon: 14,
    gap: 2,
  },
};

function palette(variant: Variant, color: Color, disabled: boolean): CSSProperties {
  if (disabled) {
    if (variant === "solid")
      return { background: "var(--interaction-disable)", color: "var(--label-disable)", boxShadow: "none" };
    if (variant === "outlined")
      return {
        background: "transparent",
        color: "var(--label-disable)",
        boxShadow: "inset 0 0 0 1px var(--line-normal-alternative)",
      };
    return { background: "transparent", color: "var(--label-disable)", boxShadow: "none" };
  }
  const primary = color === "primary";
  if (variant === "solid") {
    return primary
      ? { background: "var(--primary-normal)", color: "var(--static-white)", boxShadow: "none" }
      : { background: "var(--fill-strong)", color: "var(--label-normal)", boxShadow: "none" };
  }
  if (variant === "outlined") {
    return primary
      ? {
          background: "var(--background-normal-normal)",
          color: "var(--primary-normal)",
          boxShadow: "inset 0 0 0 1px var(--primary-normal)",
        }
      : {
          background: "var(--background-normal-normal)",
          color: "var(--label-normal)",
          boxShadow: "inset 0 0 0 1px var(--line-normal-neutral)",
        };
  }
  // text
  return primary
    ? { background: "transparent", color: "var(--primary-normal)", boxShadow: "none" }
    : { background: "transparent", color: "var(--label-normal)", boxShadow: "none" };
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  variant?: Variant;
  color?: Color;
  size?: Size;
  disabled?: boolean;
  fullWidth?: boolean;
  iconLeft?: IconName;
  iconRight?: IconName;
  iconOnly?: IconName;
  children?: ReactNode;
}

export function Button({
  children,
  variant = "solid",
  color = "primary",
  size = "medium",
  disabled = false,
  fullWidth = false,
  iconLeft,
  iconRight,
  iconOnly,
  style,
  ...rest
}: ButtonProps) {
  const s = SIZES[size] || SIZES.medium;
  const pal = palette(variant, color, disabled);
  const square = !!iconOnly;
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: s.gap,
        height: s.height,
        width: fullWidth ? "100%" : square ? s.height : undefined,
        padding: square ? 0 : `0 ${s.padX}px`,
        borderRadius: s.radius,
        border: "none",
        fontFamily: "var(--font-sans)",
        fontSize: s.font,
        lineHeight: s.lh,
        fontWeight: "var(--font-weight-semibold)",
        letterSpacing: "var(--letter-spacing-body-2)",
        whiteSpace: "nowrap",
        cursor: disabled ? "not-allowed" : "pointer",
        transition:
          "background var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard), opacity var(--duration-fast) var(--ease-standard)",
        WebkitTapHighlightColor: "transparent",
        ...pal,
        ...style,
      }}
      {...rest}
    >
      {iconOnly ? (
        <Icon name={iconOnly} size={s.icon} />
      ) : (
        <>
          {iconLeft && <Icon name={iconLeft} size={s.icon} />}
          {children}
          {iconRight && <Icon name={iconRight} size={s.icon} />}
        </>
      )}
    </button>
  );
}

export default Button;
